import React, { useEffect, useState, useCallback } from 'react';
import {
  getOpenQuestions,
  answerQuestion,
  dismissQuestion,
  QaOpenQuestion,
} from '../../services/api';

// The CENTRAL open-questions queue — the fix for "I can't find where to answer
// them." The per-thread OpenQuestion card only shows a thread's questions inside
// its ConversationCard; this lists EVERY open question across all threads
// (getOpenQuestions() with no threadId -> the full queue) with the customer /
// thread it came from and an inline Answer / Dismiss. It reuses the SAME service
// calls (answerQuestion / dismissQuestion) and the same isolated per-card state
// approach as OpenQuestion.tsx so acting on one never disturbs another.

const QueueCard: React.FC<{ question: QaOpenQuestion; onAfterAction: () => void }> = ({ question, onAfterAction }) => {
  const [answer, setAnswer] = useState('');
  const [scope, setScope] = useState<'general' | 'customer'>(
    // Default to customer-scope when we know the customer (the common case for a
    // central queue where a rep is curating a specific account); else general.
    question.customer_id != null ? 'customer' : 'general'
  );
  const [busyKind, setBusyKind] = useState<null | 'answer' | 'dismiss'>(null);
  const busy = busyKind !== null;
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (done) return null;

  const act = async (kind: 'answer' | 'dismiss', fn: () => Promise<any>) => {
    setBusyKind(kind);
    setError('');
    try {
      await fn();
      setDone(true);
      onAfterAction();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Action failed');
    } finally {
      setBusyKind(null);
    }
  };

  const answerEmpty = answer.trim() === '';
  const textareaId = `aiinfo-qa-answer-${question.id}`;
  // 'customer' scope needs a customer on the question (backend rejects otherwise).
  const customerScopeBlocked = scope === 'customer' && question.customer_id == null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <span className="text-xs font-semibold text-amber-800">❓ AI needs an answer to learn</span>
        {question.category && <span className="text-xs text-gray-500">· {question.category}</span>}
        <span className="ml-auto text-xs text-gray-500">
          {question.customer_id != null ? 'Customer #' + question.customer_id : 'No customer'}
          {question.thread_id != null && <span className="text-gray-400"> · thread #{question.thread_id}</span>}
        </span>
      </div>
      <p className="text-sm text-gray-800 mb-2">{question.question_text}</p>
      <label htmlFor={textareaId} className="sr-only">Answer to: {question.question_text}</label>
      <textarea
        id={textareaId}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={3}
        disabled={busy}
        placeholder="Type the answer so the AI can use it next time…"
        className="w-full text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
      />
      <fieldset className="mt-2" disabled={busy}>
        <legend className="text-xs font-medium text-gray-600 mb-1">Who should this answer apply to?</legend>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name={`aiinfo-qa-scope-${question.id}`}
              value="general"
              checked={scope === 'general'}
              onChange={() => setScope('general')}
              className="text-amber-600 focus:ring-amber-500"
            />
            General — everyone
          </label>
          <label
            className={'flex items-center gap-1.5 text-sm cursor-pointer ' +
              (question.customer_id == null ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700')}
          >
            <input
              type="radio"
              name={`aiinfo-qa-scope-${question.id}`}
              value="customer"
              checked={scope === 'customer'}
              disabled={question.customer_id == null}
              onChange={() => setScope('customer')}
              className="text-amber-600 focus:ring-amber-500"
            />
            This customer only
          </label>
        </div>
      </fieldset>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      <div className="flex items-center justify-end gap-2 mt-2">
        <button
          onClick={() => act('dismiss', () => dismissQuestion(question.id))}
          disabled={busy}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-red-600 disabled:opacity-50"
        >
          {busyKind === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </button>
        <button
          onClick={() => act('answer', () => answerQuestion(question.id, answer.trim(), scope))}
          disabled={busy || answerEmpty || customerScopeBlocked}
          className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-full hover:bg-amber-700 disabled:opacity-50"
        >
          {busyKind === 'answer' ? 'Saving…' : 'Answer'}
        </button>
      </div>
    </div>
  );
};

const OpenQuestionsQueue: React.FC = () => {
  const [questions, setQuestions] = useState<QaOpenQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // No threadId -> the FULL open-questions queue across every thread.
      const qs = await getOpenQuestions();
      setQuestions(qs || []);
    } catch (err) {
      setError('Failed to load open questions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refetch after any action so an answered/dismissed question drops out.
  const afterAction = () => { load(); };

  return (
    <div className="flex-1 flex flex-col">
      <div className="bg-white px-3 sm:px-6 pt-3 sm:pt-4 border-b border-gray-300">
        <div className="flex flex-wrap items-end gap-2 sm:gap-3 pb-3">
          <div className="mr-auto">
            <h2 className="text-lg font-bold text-gray-900">Open questions</h2>
            <p className="text-xs text-gray-500">Everything the AI couldn't answer, across all threads — answer to teach it, or dismiss</p>
          </div>
          <button
            onClick={() => load()}
            className="flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50">
        <div className="w-full max-w-3xl mx-auto space-y-3">
          {loading && questions.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
          )}

          {!loading && !error && questions.length === 0 && (
            <div className="text-center py-12 text-gray-500">No open questions — the AI is all caught up</div>
          )}

          {questions.map((q) => (
            <QueueCard key={q.id} question={q} onAfterAction={afterAction} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OpenQuestionsQueue;
